import type { UploadManifest } from "@/lib/storage/upload-manifest-store";
import {
  SESSION_RECORD_STORE_NAME,
  openSurfClipsAIDb,
} from "@/lib/storage/indexed-db";
import { type Phase, parsePhase } from "@/lib/sessions/phase";

export type SessionRecord = {
  clientSessionId: string;
  sessionToken: string;
  createdAt: number;
  updatedAt: number;
  phase: Phase;
  fileCount: number;
  fileNames: string[];
  bytesTotal: number;
  /** Optional user-provided name. Falls back to a summary when unset. */
  title?: string;
  /**
   * Read-only share capability token returned at session creation. Lets the
   * owner build the public share link. Only the session record holds it (it is
   * never in the upload manifest), so it is preserved across manifest-derived
   * upserts the same way the title is.
   */
  shareToken?: string;
  /**
   * Whether the user has explicitly paused this session's upload. Sticky across
   * refreshes and tabs so a paused upload is not auto-resumed by the
   * orchestrator until the user resumes it. Only the session record holds it
   * (never the manifest), so it is preserved across manifest-derived upserts.
   */
  paused?: boolean;
};

export type SessionRecordInput = Omit<SessionRecord, "createdAt" | "updatedAt"> & {
  createdAt?: number;
  updatedAt?: number;
};

/** A raw stored record; `phase` is a plain string until validated on read. */
type StoredSessionRecord = Omit<SessionRecord, "phase"> & { phase?: string };

/** Validate a stored record's phase string into the current `Phase`. */
function toSessionRecord(raw: StoredSessionRecord): SessionRecord {
  return { ...raw, phase: parsePhase(raw.phase) };
}

const SESSION_REGISTRY_CHANGED_EVENT = "surf-clips-ai:session-registry-changed";

export function notifySessionRegistryChanged() {
  window.dispatchEvent(new Event(SESSION_REGISTRY_CHANGED_EVENT));
}

export function subscribeSessionRegistryChanged(listener: () => void) {
  window.addEventListener(SESSION_REGISTRY_CHANGED_EVENT, listener);
  return () => window.removeEventListener(SESSION_REGISTRY_CHANGED_EVENT, listener);
}

export function sessionRecordFromManifest(manifest: UploadManifest): SessionRecordInput {
  const fileNames = manifest.files.map((file) => file.name);
  const bytesTotal = manifest.files.reduce((sum, file) => sum + file.sizeBytes, 0);
  const hasFailed = manifest.files.some((file) => file.status === "failed");
  const allUploaded = manifest.files.every((file) => file.status === "uploaded");

  // Locally we only know the upload stage. A failed upload keeps the session in
  // the upload view (so the user can retry); once every clip is uploaded the
  // server takes over and advances it through processing and grouping.
  let phase: Phase = "upload";
  if (allUploaded) {
    phase = "processing_pending";
  } else if (hasFailed) {
    phase = "upload_failed";
  }

  return {
    clientSessionId: manifest.clientSessionId,
    sessionToken: manifest.sessionToken,
    phase,
    fileCount: manifest.files.length,
    fileNames: fileNames.slice(0, 3),
    bytesTotal,
    createdAt: manifest.createdAt,
  };
}

function sortByUpdatedAtDesc(records: SessionRecord[]) {
  return [...records].sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function upsertSessionRecord(input: SessionRecordInput): Promise<SessionRecord> {
  const existing = await loadSessionRecord(input.clientSessionId);
  const now = Date.now();
  const record: SessionRecord = {
    ...input,
    // The record is the single home for the token. Updates derived from a
    // manifest may not carry it, so keep the established token rather than
    // clobbering it with an empty value.
    sessionToken: input.sessionToken || existing?.sessionToken || "",
    // Manifest-derived upserts don't carry a title; keep the one already synced
    // from the server (the source of truth) instead of clobbering it.
    title: input.title ?? existing?.title,
    // The share token only arrives once (at creation) and never via a manifest,
    // so preserve the established value rather than dropping it on later upserts.
    shareToken: input.shareToken ?? existing?.shareToken,
    // Manifest-derived upserts (which fire frequently during upload) don't carry
    // the pause flag, so keep the established value rather than clobbering it.
    paused: input.paused ?? existing?.paused,
    createdAt: input.createdAt ?? existing?.createdAt ?? now,
    updatedAt: input.updatedAt ?? now,
  };

  const db = await openSurfClipsAIDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(SESSION_RECORD_STORE_NAME, "readwrite");
      tx.objectStore(SESSION_RECORD_STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(tx.error ?? new Error("Failed to save session record"));
    });
  } finally {
    db.close();
  }

  return record;
}

/**
 * Persist whether a session's upload is paused. Sticky so a refresh or another
 * tab does not auto-resume it until the user explicitly resumes.
 */
export async function setSessionPaused(clientSessionId: string, paused: boolean) {
  const existing = await loadSessionRecord(clientSessionId);
  if (!existing) return null;
  return upsertSessionRecord({ ...existing, paused });
}

export async function loadSessionRecord(clientSessionId: string) {
  const db = await openSurfClipsAIDb();
  try {
    return await new Promise<SessionRecord | null>((resolve, reject) => {
      const tx = db.transaction(SESSION_RECORD_STORE_NAME, "readonly");
      const request = tx.objectStore(SESSION_RECORD_STORE_NAME).get(clientSessionId);
      request.onerror = () =>
        reject(request.error ?? new Error("Failed to load session record"));
      request.onsuccess = () => {
        const raw = request.result as StoredSessionRecord | undefined;
        if (!raw) {
          resolve(null);
          return;
        }
        resolve(toSessionRecord(raw));
      };
    });
  } finally {
    db.close();
  }
}

/**
 * Canonical read for a session's token. The session record is the durable
 * home for the token (it outlives the transient upload manifest), so auth and
 * resume logic should resolve the token through here rather than trusting the
 * manifest's copy. Returns null when there is no usable token.
 */
export async function loadSessionToken(clientSessionId: string) {
  const record = await loadSessionRecord(clientSessionId);
  return record?.sessionToken ? record.sessionToken : null;
}

export async function listSessionRecords() {
  const db = await openSurfClipsAIDb();
  try {
    const records = await new Promise<SessionRecord[]>((resolve, reject) => {
      const tx = db.transaction(SESSION_RECORD_STORE_NAME, "readonly");
      const request = tx.objectStore(SESSION_RECORD_STORE_NAME).getAll();
      request.onerror = () =>
        reject(request.error ?? new Error("Failed to list session records"));
      request.onsuccess = () =>
        resolve(
          (request.result as StoredSessionRecord[]).map(toSessionRecord),
        );
    });
    return sortByUpdatedAtDesc(records);
  } finally {
    db.close();
  }
}

export async function deleteSessionRecord(clientSessionId: string) {
  const db = await openSurfClipsAIDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(SESSION_RECORD_STORE_NAME, "readwrite");
      tx.objectStore(SESSION_RECORD_STORE_NAME).delete(clientSessionId);
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(tx.error ?? new Error("Failed to delete session record"));
    });
  } finally {
    db.close();
  }
}
